import type { ReactNode } from 'react';
import type { TableProps } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { KeyRound, MonitorSmartphone, ShieldCheck, Smartphone, UserRound } from 'lucide-react';

import { DataTable, MenuThaoTac, PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Separator, Skeleton } from '../../../components/ui';
import { useTrangThaiKenhSuKien } from '../../../hooks/useKenhSuKien';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { KHONG_CO, ngayGioVN, ngayVN } from '../../../lib/dinh-dang';
import { useAuthStore } from '../../../stores/auth';
import { useUiStore } from '../../../stores/ui';
import { TONE_KENH_SU_KIEN, TONE_MA_KHOI_PHUC, TONE_XAC_THUC_HAI_LOP } from '../constants';
import {
  useDangXuatPhien,
  useKetQuaHanhDong,
  useMoHanhDong,
  usePhienDangNhap,
  useXacThucCuaToi,
} from '../hooks/useTaiKhoan';
import { hienMucMaKhoiPhuc, nutMaKhoiPhuc } from '../nghiep-vu/ma-khoi-phuc';
import type { MaKhoiPhuc, PhienDangNhap, UngDungXacThuc } from '../types';

/** Một dòng thuộc tính của ô hồ sơ — cùng dáng với màn chi tiết tenant. */
function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-1 border-b border-default py-2.5 last:border-0">
      <span className="w-32 shrink-0 text-xs text-content-muted">{nhan}</span>
      <span className="min-w-0 flex-1 break-words text-sm text-content-primary">{children}</span>
    </div>
  );
}

/** Dải lỗi kèm nút thử lại, đặt ngay trong ô bị lỗi. */
function DaiLoi({ cau, onThuLai }: { cau: string; onThuLai: () => void }) {
  const { t } = useT();
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-danger-50 px-4 py-3">
      <span className="text-sm text-danger-700">{cau}</span>
      <Button variant="outline" size="sm" onClick={onThuLai}>
        {t('chung.thuLai')}
      </Button>
    </div>
  );
}

/**
 * Báo kết quả action vừa quay về từ trang đăng nhập (`kc_action_status`).
 * Huỷ giữa chừng không phải lỗi: nói trung tính, không tô đỏ.
 */
function DaiKetQua() {
  const { t } = useT();
  const { ketQua, boQua } = useKetQuaHanhDong();
  if (!ketQua) return null;

  const hanhDong = ketQua.hanhDong ?? '';
  const loai = hanhDong.startsWith('delete_credential')
    ? 'goOtp'
    : hanhDong === 'UPDATE_PASSWORD'
      ? 'doiMatKhau'
      : hanhDong === 'CONFIGURE_RECOVERY_AUTHN_CODES'
        ? 'maKhoiPhuc'
        : 'caiOtp';
  const khoa: Record<typeof ketQua.trangThai, Record<typeof loai, KhoaDich>> = {
    success: {
      caiOtp: 'taiKhoan.ketQua.daCaiOtp',
      goOtp: 'taiKhoan.ketQua.daGoOtp',
      doiMatKhau: 'taiKhoan.ketQua.daDoiMatKhau',
      maKhoiPhuc: 'taiKhoan.ketQua.daTaoMaKhoiPhuc',
    },
    cancelled: {
      caiOtp: 'taiKhoan.ketQua.huyCaiOtp',
      goOtp: 'taiKhoan.ketQua.huyGoOtp',
      doiMatKhau: 'taiKhoan.ketQua.huyDoiMatKhau',
      maKhoiPhuc: 'taiKhoan.ketQua.huyTaoMaKhoiPhuc',
    },
    error: {
      caiOtp: 'taiKhoan.ketQua.loi',
      goOtp: 'taiKhoan.ketQua.loi',
      doiMatKhau: 'taiKhoan.ketQua.loi',
      maKhoiPhuc: 'taiKhoan.ketQua.loi',
    },
  };
  const mau = {
    success: 'border-success-200 bg-success-50 text-success-700',
    cancelled: 'border-default bg-surface text-content-secondary',
    error: 'border-danger-200 bg-danger-50 text-danger-700',
  }[ketQua.trangThai];

  return (
    <div
      role={ketQua.trangThai === 'error' ? 'alert' : 'status'}
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${mau}`}
    >
      <span>{t(khoa[ketQua.trangThai][loai])}</span>
      <Button variant="ghost" size="sm" onClick={boQua}>
        {t('taiKhoan.ketQua.anThongBao')}
      </Button>
    </div>
  );
}

function OHoSo() {
  const { t } = useT();
  const user = useAuthStore((s) => s.user);
  // Cùng luật với thanh điều hướng: Keycloak trả khoá `vaiTro.*`, tài khoản mẫu
  // có thể mang chữ sẵn.
  const vaiTroGoc = user?.roles[0];
  const vaiTro = vaiTroGoc?.startsWith('vaiTro.') ? t(vaiTroGoc as KhoaDich) : vaiTroGoc;

  return (
    <Tile span="col-span-12 lg:col-span-5">
      <TileHeader icon={<UserRound size={15} />} tone="primary" title={t('taiKhoan.hoSo')} />
      <TileBody>
        <Dong nhan={t('taiKhoan.hoTen')}>{user?.fullName || KHONG_CO}</Dong>
        {/* ADR-0101: tên đăng nhập và email nhận thư hiện riêng, chỉ đọc; quản trị đổi email nhận thư. */}
        <Dong nhan={t('taiKhoan.tenDangNhap')}>
          <span className="break-all">{user?.tenDangNhap || user?.email || KHONG_CO}</span>
        </Dong>
        <Dong nhan={t('taiKhoan.emailNhanThu')}>
          <span className="break-all">{user?.email || KHONG_CO}</span>
        </Dong>
        <Dong nhan={t('taiKhoan.vaiTro')}>{vaiTro || KHONG_CO}</Dong>
        <p className="mt-3 text-xs leading-relaxed text-content-muted">{t('taiKhoan.hoSoGhiChu')}</p>
      </TileBody>
    </Tile>
  );
}

/**
 * Mục "Mã khôi phục" — lối dự phòng khi mất điện thoại. Chỉ dựng khi đã cài
 * ứng dụng xác thực (`hienMucMaKhoiPhuc`); tạo và tạo lại đều mở trang
 * Keycloak bằng `CONFIGURE_RECOVERY_AUTHN_CODES`, như nút cài ứng dụng.
 */
function MucMaKhoiPhuc({ mkp, onTao }: { mkp: MaKhoiPhuc; onTao: () => void }) {
  const { t, locale } = useT();
  const taoLai = nutMaKhoiPhuc(mkp) === 'taoLai';

  return (
    <section aria-labelledby="tai-khoan-ma-khoi-phuc" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="tai-khoan-ma-khoi-phuc" className="text-sm font-semibold text-content-primary">
          {t('taiKhoan.maKhoiPhuc')}
        </h3>
        {mkp.trangThai !== 'khongRo' && (
          <Badge variant={TONE_MA_KHOI_PHUC[mkp.trangThai]}>
            {t(mkp.trangThai === 'daTao' ? 'taiKhoan.maKhoiPhucDaTao' : 'taiKhoan.maKhoiPhucChuaTao')}
          </Badge>
        )}
      </div>
      <p className="text-sm leading-relaxed text-content-secondary">{t('taiKhoan.maKhoiPhucMo')}</p>
      {taoLai && (
        <p className="text-xs leading-relaxed text-content-muted">
          {mkp.taoLuc && `${t('taiKhoan.maKhoiPhucTaoLuc', { ngay: ngayGioVN(mkp.taoLuc, locale) })} `}
          {t('taiKhoan.maKhoiPhucTaoLaiMo')}
        </p>
      )}
      <Button variant="outline" size="md" onClick={onTao}>
        {t(taoLai ? 'taiKhoan.taoLaiMaKhoiPhuc' : 'taiKhoan.taoMaKhoiPhuc')}
      </Button>
    </section>
  );
}

function ODangNhap() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const { data, isLoading, error, refetch } = useXacThucCuaToi();
  const moHanhDong = useMoHanhDong();
  const showConfirm = useUiStore((s) => s.showConfirm);

  const tenUngDung = (u: UngDungXacThuc) => u.nhan ?? t('taiKhoan.ungDungKhongTen');

  const xacNhanGo = (u: UngDungXacThuc) =>
    showConfirm({
      title: t('taiKhoan.xacNhanGoTieuDe', { ten: tenUngDung(u) }),
      body: t('taiKhoan.xacNhanGoThan'),
      nhanXacNhan: t('taiKhoan.goUngDung'),
      onOk: () => moHanhDong(`delete_credential:${u.id}`),
    });

  let noiDung: ReactNode;
  if (isLoading) {
    noiDung = (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-9 w-40" />
        <Separator />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  } else if (error || !data) {
    noiDung = <DaiLoi cau={t('taiKhoan.loiXacThuc', { loi: dichLoi(error) })} onThuLai={() => void refetch()} />;
  } else {
    const daBat = data.ungDungXacThuc.length > 0;
    // Mỗi tài khoản chỉ một ứng dụng xác thực: đã cài thì không mời cài thêm,
    // đổi điện thoại bằng cách gỡ rồi cài lại. Vì vậy gỡ luôn được, kể cả khi
    // tenant bắt buộc MFA (ADR-0015) — lần đăng nhập sau sẽ buộc cài lại.

    noiDung = (
      <div className="space-y-5">
        <section aria-labelledby="tai-khoan-mat-khau" className="space-y-2">
          <h3 id="tai-khoan-mat-khau" className="text-sm font-semibold text-content-primary">
            {t('taiKhoan.matKhau')}
          </h3>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-content-secondary">
              {data.matKhauDatLuc
                ? t('taiKhoan.matKhauDatLuc', { ngay: ngayVN(data.matKhauDatLuc) })
                : t('taiKhoan.matKhauChuaCo')}
            </p>
            <Button variant="outline" size="md" onClick={() => moHanhDong('UPDATE_PASSWORD')}>
              {t('taiKhoan.doiMatKhau')}
            </Button>
          </div>
        </section>

        <Separator />

        <section aria-labelledby="tai-khoan-otp" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="tai-khoan-otp" className="text-sm font-semibold text-content-primary">
              {t('taiKhoan.xacThucHaiLop')}
            </h3>
            <Badge variant={TONE_XAC_THUC_HAI_LOP[daBat ? 'bat' : 'chuaBat']}>
              {t(daBat ? 'taiKhoan.dangBat' : 'taiKhoan.chuaBat')}
            </Badge>
            {data.batBuocMfa && <Badge variant="neutral">{t('taiKhoan.toChucBatBuoc')}</Badge>}
          </div>
          <p className="text-sm leading-relaxed text-content-secondary">
            {t(daBat ? 'taiKhoan.otpMoDaBat' : 'taiKhoan.otpMoChuaBat')}
          </p>

          {daBat && (
            <ul className="divide-y divide-default rounded-lg border border-default">
              {data.ungDungXacThuc.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <span className="flex min-w-0 items-center gap-3">
                    <Smartphone size={16} aria-hidden className="shrink-0 text-content-muted" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-content-primary">{tenUngDung(u)}</span>
                      {u.taoLuc && (
                        <span className="block text-xs text-content-muted">
                          {t('taiKhoan.caiNgay', { ngay: ngayGioVN(u.taoLuc, locale) })}
                        </span>
                      )}
                    </span>
                  </span>
                  <Button variant="outline" size="sm" danger onClick={() => xacNhanGo(u)}>
                    {t('taiKhoan.goUngDung')}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {daBat ? (
            <p className="text-xs leading-relaxed text-content-muted">
              {t(data.batBuocMfa ? 'taiKhoan.motUngDungBatBuoc' : 'taiKhoan.motUngDung')}
            </p>
          ) : (
            <>
              {data.batBuocMfa && (
                <p className="text-xs leading-relaxed text-content-muted">{t('taiKhoan.batBuocSeHoi')}</p>
              )}
              <Button variant="primary" size="md" onClick={() => moHanhDong('CONFIGURE_TOTP')}>
                {t('taiKhoan.caiUngDung')}
              </Button>
            </>
          )}
        </section>

        {hienMucMaKhoiPhuc(data) && (
          <>
            <Separator />
            <MucMaKhoiPhuc
              mkp={data.maKhoiPhuc}
              onTao={() => moHanhDong('CONFIGURE_RECOVERY_AUTHN_CODES')}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <Tile span="col-span-12 lg:col-span-7">
      <TileHeader
        icon={<ShieldCheck size={15} />}
        tone="success"
        title={t('taiKhoan.dangNhapVaBaoMat')}
        mo={t('taiKhoan.dangNhapVaBaoMatMo')}
      />
      <TileBody>{noiDung}</TileBody>
    </Tile>
  );
}

/**
 * Nhãn trạng thái kênh sự kiện: danh sách phiên đang cập nhật trực tiếp hay
 * đang chờ nối lại. `aria-live` để trình đọc màn hình báo khi đổi trạng thái;
 * vùng live luôn có mặt, chỉ nhãn bên trong ẩn hiện.
 */
function NhanCapNhatTrucTiep() {
  const { t } = useT();
  const trangThai = useTrangThaiKenhSuKien();
  const coNhan = trangThai === 'dangKetNoi' || trangThai === 'trucTiep' || trangThai === 'matKetNoi';
  const nhan = {
    dangKetNoi: t('taiKhoan.kenhDangKetNoi'),
    trucTiep: t('taiKhoan.kenhTrucTiep'),
    matKetNoi: t('taiKhoan.kenhMatKetNoi'),
  };
  return (
    <span aria-live="polite" className="inline-flex min-w-0">
      {coNhan && <Badge variant={TONE_KENH_SU_KIEN[trangThai]}>{nhan[trangThai]}</Badge>}
    </span>
  );
}

function OPhien() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const { data, isLoading, error, refetch } = usePhienDangNhap();
  const dangXuat = useDangXuatPhien();
  const showConfirm = useUiStore((s) => s.showConfirm);

  const tenThietBi = (p: PhienDangNhap) =>
    [p.trinhDuyet, p.heDieuHanh].filter(Boolean).join(' · ') || t('taiKhoan.thietBiKhongRo');
  const soPhienKhac = (data ?? []).filter((p) => !p.hienTai).length;

  const cot: TableProps<PhienDangNhap>['columns'] = [
    {
      title: t('taiKhoan.cotThietBi'),
      key: 'thiet-bi',
      render: (_v, p) => (
        <span className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2 text-sm text-content-primary">
            {tenThietBi(p)}
            {p.hienTai && <Badge variant="primary">{t('taiKhoan.phienNay')}</Badge>}
          </span>
          {p.ungDung.length > 0 && (
            <span className="text-2xs text-content-muted">{p.ungDung.join(', ')}</span>
          )}
        </span>
      ),
    },
    {
      title: t('taiKhoan.cotIp'),
      dataIndex: 'ip',
      width: 150,
      render: (v: string) => <span className="font-mono text-xs text-content-secondary">{v || KHONG_CO}</span>,
    },
    {
      title: t('taiKhoan.cotBatDau'),
      dataIndex: 'batDauLuc',
      width: 170,
      render: (v: string) => (
        <span className="text-sm tabular-nums text-content-secondary">{ngayGioVN(v, locale)}</span>
      ),
    },
    {
      title: t('taiKhoan.cotTruyCapCuoi'),
      dataIndex: 'truyCapCuoiLuc',
      width: 170,
      render: (v: string) => (
        <span className="text-sm tabular-nums text-content-secondary">{ngayGioVN(v, locale)}</span>
      ),
    },
    {
      title: <span className="sr-only">{t('chung.thaoTac')}</span>,
      key: 'thao-tac',
      width: 56,
      align: 'right',
      render: (_v, p) => (
        <MenuThaoTac
          tenBanGhi={tenThietBi(p)}
          muc={[
            {
              khoa: 'dang-xuat',
              nhan: t('taiKhoan.dangXuatPhien'),
              icon: <LogoutOutlined />,
              // Phiên đang xem: đăng xuất bằng mục `Đăng xuất` của menu tài khoản.
              hien: !p.hienTai,
              nhom: 'pha-huy',
              onChon: () =>
                showConfirm({
                  title: t('taiKhoan.xacNhanDangXuatTieuDe', { ten: tenThietBi(p) }),
                  body: t('taiKhoan.xacNhanDangXuatThan'),
                  nhanXacNhan: t('taiKhoan.dangXuatPhien'),
                  onOk: () => dangXuat.mutate({ id: p.id }),
                }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Tile className="overflow-hidden">
      <TileHeader
        icon={<MonitorSmartphone size={15} />}
        tone="sky"
        title={t('taiKhoan.phienDangNhap')}
        mo={t('taiKhoan.phienDangNhapMo')}
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <NhanCapNhatTrucTiep />
            {soPhienKhac > 0 && (
              <Button
                variant="outline"
                size="sm"
                danger
                loading={dangXuat.isPending}
                onClick={() =>
                  showConfirm({
                    title: t('taiKhoan.xacNhanDangXuatKhacTieuDe', { so: soPhienKhac }),
                    body: t('taiKhoan.xacNhanDangXuatKhacThan'),
                    nhanXacNhan: t('taiKhoan.dangXuatPhienKhac', { so: soPhienKhac }),
                    onOk: () => dangXuat.mutate({ tatCaPhienKhac: true }),
                  })
                }
              >
                {t('taiKhoan.dangXuatPhienKhac', { so: soPhienKhac })}
              </Button>
            )}
          </div>
        }
      />
      {error ? (
        <div className="p-4">
          <DaiLoi cau={t('taiKhoan.loiPhien', { loi: dichLoi(error) })} onThuLai={() => void refetch()} />
        </div>
      ) : (
        <DataTable<PhienDangNhap>
          caption={t('taiKhoan.phienCaption')}
          rowKey="id"
          dataSource={data ?? []}
          columns={cot}
          loading={isLoading}
          size="middle"
          pagination={false}
          scroll={{ x: 760 }}
          locale={{
            emptyText: isLoading ? ' ' : <Empty icon={MonitorSmartphone} title={t('taiKhoan.phienRong')} />,
          }}
        />
      )}
    </Tile>
  );
}

/**
 * "Tài khoản của tôi" — chỗ duy nhất người dùng tự quản tài khoản (ADR-0027),
 * dùng chung cho cả hai cổng ở `/cai-dat`, ngoài hệ tiền tố `/admin`, `/client`.
 *
 * Đổi mật khẩu, cài và gỡ ứng dụng xác thực mở trang đăng nhập bằng
 * application-initiated action (ADR-0028 mục 2) rồi quay về đây; danh sách
 * OTP và phiên đọc từ Account REST API của Keycloak (tạm, xem repository).
 */
export function TaiKhoanCuaToiView() {
  const { t } = useT();

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          titleIcon={<KeyRound size={18} className="text-primary-600" />}
          title={t('taiKhoan.tieuDe')}
          subtitle={t('taiKhoan.moTa')}
        />
        <DaiKetQua />
        <div className="grid grid-cols-12 gap-4">
          <OHoSo />
          <ODangNhap />
          <OPhien />
        </div>
      </div>
    </div>
  );
}
